import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const FeaturesSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.backgroundSecondary};
  position: relative;
  overflow: hidden;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 20%;
  right: -200px;
  width: 600px;
  height: 600px;
  background: ${props => props.theme.gradients.glow};
  filter: blur(120px);
  opacity: 0.4;
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const SectionLabel = styled(motion.span)`
  display: inline-block;
  padding: 8px 16px;
  background: ${props => props.theme.gradients.subtle};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.accentPurple};
  margin-bottom: ${props => props.theme.spacing.md};
  letter-spacing: 0.02em;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  margin-bottom: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 1.5vw, 1.125rem);
  color: ${props => props.theme.colors.subtextGray};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const CategoryNav = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 6px;
  }
`;

const CategoryTab = styled(motion.button)`
  padding: 10px 18px;
  background: ${p => p.active ? p.theme.gradients.primary : p.theme.colors.surfaceCard};
  border: 1px solid ${p => p.active ? 'transparent' : p.theme.colors.borderSubtle};
  border-radius: 100px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: ${p => p.theme.typography.weights.semibold};
  color: ${p => p.active ? 'white' : p.theme.colors.textDark};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${p => p.active ? 'transparent' : p.theme.colors.accentPurple};
    transform: translateY(-1px);
  }

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: 8px 14px;
    font-size: 0.8125rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.md};
  }
`;

const FeatureCard = styled(motion.div)`
  background: ${props => props.theme.colors.surfaceCard};
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  gap: ${props => props.theme.spacing.md};
  align-items: flex-start;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    border-color: ${props => props.theme.colors.accentPurple};
    box-shadow: ${props => props.theme.shadows.cardHover};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.md};
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 12px;
  background: ${props => props.theme.gradients.subtle};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.accentPurple};
`;

const FeatureContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const FeatureTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 6px;
`;

const FeatureTitle = styled.h3`
  font-size: 1.0625rem;
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.textLight};
  letter-spacing: -0.01em;
  margin: 0;
`;

const FeatureBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: ${p => p.theme.typography.weights.semibold};
  padding: 3px 8px;
  border-radius: 100px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${p =>
    p.variant === 'ai'
      ? 'linear-gradient(135deg, rgba(124,58,237,0.18), rgba(167,139,250,0.18))'
      : p.variant === 'cert'
        ? 'rgba(34,197,94,0.15)'
        : p.variant === 'game'
          ? 'rgba(6,182,212,0.15)'
          : 'rgba(245,158,11,0.15)'};
  color: ${p =>
    p.variant === 'ai'
      ? p.theme.colors.accentPurple
      : p.variant === 'cert'
        ? '#16A34A'
        : p.variant === 'game'
          ? '#0891B2'
          : '#D97706'};
  border: 1px solid ${p =>
    p.variant === 'ai'
      ? 'rgba(124,58,237,0.3)'
      : p.variant === 'cert'
        ? 'rgba(34,197,94,0.3)'
        : p.variant === 'game'
          ? 'rgba(6,182,212,0.3)'
          : 'rgba(245,158,11,0.3)'};
`;

const FeatureDescription = styled.p`
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.subtextGray};
  line-height: 1.5;
  margin: 0;
`;

/* ── Tool icons (24x24) ── */
const AwardIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="8" r="6" />
    <path d="M8.5 13L7 22l5-3 5 3-1.5-9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BookIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 4.5A2.5 2.5 0 014.5 2H20v18H6.5A2.5 2.5 0 014 22.5M2 4.5V20a2.5 2.5 0 002.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GameIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <path d="M6 12h4M8 10v4M15 11h.01M18 13h.01" strokeLinecap="round" />
  </svg>
);

const HeartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TargetCrosshairIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
  </svg>
);

const CompassIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" strokeLinejoin="round" />
  </svg>
);

const ClipboardListIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="6" y="3" width="12" height="18" rx="2" />
    <path d="M9 3h6v3H9zM9 11h6M9 15h6M9 7h6" strokeLinecap="round" />
  </svg>
);

const ClipboardCheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="6" y="3" width="12" height="18" rx="2" />
    <path d="M9 3h6v3H9zM9 13l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UsersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="9" cy="7" r="3" />
    <circle cx="17" cy="7" r="3" />
    <path d="M3 21C3 17 6 14 9 14C10.5 14 11.5 14.5 12 15" strokeLinecap="round" />
    <path d="M21 21C21 17 18 14 15 14" strokeLinecap="round" />
  </svg>
);

const BotIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="8" width="16" height="12" rx="3" />
    <path d="M12 4v4M9 14h.01M15 14h.01M9 18h6" strokeLinecap="round" />
  </svg>
);

const PeopleCompassIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeLinecap="round" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const GamepadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M6 11h4M8 9v4M14 12h.01M17 13h.01" strokeLinecap="round" />
    <rect x="2" y="6" width="20" height="12" rx="6" />
  </svg>
);

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'learn', label: 'Learn' },
  { id: 'plan', label: 'Plan' },
  { id: 'execute', label: 'Execute' },
  { id: 'improve', label: 'Improve' },
];

const TOOLS = [
  // Learn
  { id: 'white-belt', icon: AwardIcon, title: 'White Belt', description: 'Earn your Lean White Belt across 8 interactive modules with a shareable credential.', category: 'learn', badge: { variant: 'cert', label: 'Cert' } },
  { id: 'rfp', icon: HeartIcon, title: 'Respect for People', description: 'Behavioral training and habit-building for workplace culture change.', category: 'learn', badge: { variant: 'cert', label: 'Cert' } },
  { id: 'library', icon: BookIcon, title: 'Library', description: 'Daily cards, flashcards, cheat sheets, and scenario challenges for bite-sized learning.', category: 'learn' },
  { id: 'waste-hunt', icon: GameIcon, title: 'Intro to Lean', description: 'Four interactive Lean lessons ending with the Waste Hunt capstone game.', category: 'learn', badge: { variant: 'game', label: 'Game' } },
  { id: 'bottleneck', icon: GamepadIcon, title: 'Bottleneck Game', description: 'Theory of Constraints simulation — identify and fix production bottlenecks.', category: 'learn', badge: { variant: 'game', label: 'Game' } },

  // Plan
  { id: 'hoshin', icon: TargetCrosshairIcon, title: 'Hoshin Kanri', description: 'Strategy deployment, X-matrix planning, and monthly review tracking.', category: 'plan' },
  { id: 'charter', icon: CompassIcon, title: 'Charter Pilot', description: 'Project creation, charter management, and team collaboration.', category: 'plan' },

  // Execute
  { id: 'job-instructor', icon: ClipboardListIcon, title: 'Job Instructor', description: 'AI converts SOPs into TWI Job Instructions with auto-detected hazards and quizzes.', category: 'execute', badge: { variant: 'ai', label: 'AI' } },
  { id: 'audits', icon: ClipboardCheckIcon, title: 'Audits & Checklists', description: 'Create, share, and analyze audit checklists with public response collection.', category: 'execute' },
  { id: 'teams', icon: UsersIcon, title: 'Teams', description: 'Create teams, invite members, and manage shared activity, leaderboards, and credits.', category: 'execute' },

  // Improve
  { id: 'neal', icon: BotIcon, title: 'NEAL', description: 'Conversational AI assistant that coaches your A3s and surfaces process intelligence.', category: 'improve', badge: { variant: 'ai', label: 'AI' } },
  { id: 'people-compass', icon: PeopleCompassIcon, title: 'People Compass', description: 'DISC assessments, real team insights, and behavioral coaching.', category: 'improve' },
];

const Features = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const visibleTools = useMemo(() => {
    if (activeCategory === 'all') return TOOLS;
    return TOOLS.filter((tool) => tool.category === activeCategory);
  }, [activeCategory]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <FeaturesSection id="tools">
      <BackgroundGlow />
      <Container>
        <SectionHeader>
          <SectionLabel
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Tools
          </SectionLabel>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            One Platform. Every Tool.
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Continuous improvement teams use Continua Labs to learn, plan, execute, and improve — all in one workspace.
          </SectionSubtitle>
        </SectionHeader>

        <CategoryNav>
          {CATEGORIES.map((category) => (
            <CategoryTab
              key={category.id}
              active={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {category.label}
            </CategoryTab>
          ))}
        </CategoryNav>

        <FeaturesGrid
          as={motion.div}
          key={activeCategory}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {visibleTools.map((tool) => {
            const IconComponent = tool.icon;
            return (
              <FeatureCard key={tool.id} variants={itemVariants} layout>
                <IconWrapper>
                  <IconComponent />
                </IconWrapper>
                <FeatureContent>
                  <FeatureTitleRow>
                    <FeatureTitle>{tool.title}</FeatureTitle>
                    {tool.badge && (
                      <FeatureBadge variant={tool.badge.variant}>{tool.badge.label}</FeatureBadge>
                    )}
                  </FeatureTitleRow>
                  <FeatureDescription>{tool.description}</FeatureDescription>
                </FeatureContent>
              </FeatureCard>
            );
          })}
        </FeaturesGrid>
      </Container>
    </FeaturesSection>
  );
};

export default Features;
